import { useState, useMemo, useRef, useEffect } from "react";
import { useI18n } from "@/hooks/useI18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AutocompleteSelect from "@/components/ui/autocomplete-select";
import { COUNTRIES } from "@/constants/countries";
import { Slider } from "@/components/ui/slider";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Search, Loader2, MapPin, Users, X, Star, ChevronLeft, ChevronRight,
  RotateCcw, ChevronDown, Minus, Plus, SlidersHorizontal,
} from "lucide-react";
import { CityAutocomplete } from "../../shared/CityAutocomplete";
import { SortResultsButton, type SortField, type SortDir } from "../../shared/SortResultsButton";
import type { RateHawkSearchResult, RateHawkSearchFilter, RateHawkRoomGuestConfig } from "../types";
import { Skeleton } from "@/components/ui/skeleton";
import { SearchResultCard, SearchResultCardSkeleton } from "./SearchResultCard";

const MAX_ROOMS = 9;
const MAX_ADULTS_PER_ROOM = 20;
const MAX_CHILDREN_PER_ROOM = 10;
const DEFAULT_CHILD_AGE = 10;

const KIND_OPTIONS = ["Hotel", "Hostel", "Apartment", "Villa", "Resort", "Guest house", "Motel"];

const MEAL_OPTIONS: { value: string; label: string }[] = [
  { value: "nomeal", label: "Room only" },
  { value: "breakfast", label: "Breakfast" },
  { value: "halfboard", label: "Half board" },
  { value: "fullboard", label: "Full board" },
  { value: "all-inclusive", label: "All inclusive" },
];

function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// ─── Amenity labels ───────────────────────────────────────────────────────────

const AMENITY_LABELS: Record<string, string> = {
  wifi: "Wi-Fi",
  parking: "Parking",
  pool: "Pool",
  gym: "Gym",
  spa: "Spa",
  restaurant: "Restaurant",
  pets_allowed: "Pets allowed",
  "24_hour_front_desk": "24h Front Desk",
  concierge: "Concierge",
  room_service: "Room Service",
  business_centre: "Business Centre",
  air_conditioning: "A/C",
  bar: "Bar",
  breakfast_included: "Breakfast",
};

// ─── Props ────────────────────────────────────────────────────────────────────

interface SearchWithResultsProps {
  cityName: string;
  setCityName: (v: string) => void;
  coordinates: { lat: number; lng: number } | null;
  setCoordinates: (v: { lat: number; lng: number } | null) => void;
  checkIn: string;
  setCheckIn: (v: string) => void;
  checkOut: string;
  setCheckOut: (v: string) => void;
  rooms: RateHawkRoomGuestConfig[];
  setRooms: (v: RateHawkRoomGuestConfig[]) => void;
  radius: number;
  setRadius: (v: number) => void;
  filter: RateHawkSearchFilter;
  onFilterChange: (f: RateHawkSearchFilter) => void;
  /** Total hotels available from the API (may exceed results.length if capped at 200). */
  totalHotels?: number | null;
  canSearch: boolean;
  isSearching: boolean;
  onSearch: () => void;
  results: RateHawkSearchResult[];
  searchError: string | null;
  onSelectResult: (result: RateHawkSearchResult) => void;
  startDate?: string | null;
  endDate?: string | null;
  residency: string;
  onResidencyChange: (v: string) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SearchWithResults({
  cityName,
  setCityName,
  coordinates,
  setCoordinates,
  checkIn,
  setCheckIn,
  checkOut,
  setCheckOut,
  rooms,
  setRooms,
  radius,
  setRadius,
  filter,
  onFilterChange,
  totalHotels,
  canSearch,
  isSearching,
  onSearch,
  results,
  searchError,
  onSelectResult,
  startDate,
  endDate,
  residency,
  onResidencyChange,
}: SearchWithResultsProps) {
  const { t } = useI18n();

  // Client-side display filters (instant, no re-search needed)
  const [nameFilter, setNameFilter] = useState("");
  const [selectedAmenities, setSelectedAmenities] = useState<string[]>([]);
  const [sortField, setSortField] = useState<SortField>("price");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [guestsOpen, setGuestsOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const guestsRef = useRef<HTMLDivElement>(null);

  // Helpers to update individual server-side filter fields
  const setMinStarRating = (v: number) =>
    onFilterChange({ ...filter, min_star_rating: v === filter.min_star_rating ? undefined : v });
  const toggleKind = (k: string) => {
    const cur = filter.kind ?? [];
    onFilterChange({ ...filter, kind: cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k] });
  };
  const toggleMeal = (m: string) => {
    const cur = filter.meal_type ?? [];
    onFilterChange({ ...filter, meal_type: cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m] });
  };

  // Per-room guest helpers — each room's adults/children are edited independently.
  const updateRoom = (index: number, patch: Partial<RateHawkRoomGuestConfig>) => {
    setRooms(rooms.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };
  const addRoom = () => {
    if (rooms.length >= MAX_ROOMS) return;
    setRooms([...rooms, { adults: 1, childrenAges: [] }]);
  };
  const removeRoom = (index: number) => {
    if (rooms.length <= 1) return;
    setRooms(rooms.filter((_, i) => i !== index));
  };
  const addChild = (index: number) => {
    const room = rooms[index];
    if (room.childrenAges.length >= MAX_CHILDREN_PER_ROOM) return;
    updateRoom(index, { childrenAges: [...room.childrenAges, DEFAULT_CHILD_AGE] });
  };
  const removeChild = (index: number, childIndex: number) => {
    const room = rooms[index];
    updateRoom(index, { childrenAges: room.childrenAges.filter((_, i) => i !== childIndex) });
  };
  const updateChildAge = (index: number, childIndex: number, age: number) => {
    const room = rooms[index];
    const clamped = Math.max(0, Math.min(17, age));
    updateRoom(index, {
      childrenAges: room.childrenAges.map((a, i) => (i === childIndex ? clamped : a)),
    });
  };

  const totalAdults = rooms.reduce((sum, r) => sum + r.adults, 0);
  const totalChildren = rooms.reduce((sum, r) => sum + r.childrenAges.length, 0);
  const selectedCountry = COUNTRIES.find((c) => c.code.toLowerCase() === residency);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      // La select nazionalità (Radix Popover) renderizza il suo contenuto in un
      // portal fuori da guestsRef: non trattare i click al suo interno come "outside".
      if (
        target instanceof Element &&
        target.closest("[data-radix-popper-content-wrapper]")
      ) {
        return;
      }
      if (guestsRef.current && !guestsRef.current.contains(target)) {
        setGuestsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const availableAmenities = useMemo(() => {
    const seen = new Set<string>();
    for (const r of results) {
      for (const a of r.amenities ?? []) seen.add(a);
    }
    return Array.from(seen).sort();
  }, [results]);

  // All filters applied client-side (instant, no re-search needed)
  const filteredResults = useMemo(() => {
    let arr = results;
    if (nameFilter.trim()) {
      const q = nameFilter.trim().toLowerCase();
      arr = arr.filter((r) => r.name.toLowerCase().includes(q));
    }
    if (selectedAmenities.length > 0) {
      arr = arr.filter((r) => selectedAmenities.every((a) => r.amenities?.includes(a)));
    }
    if (filter.min_star_rating) {
      arr = arr.filter((r) => (r.starRating ?? 0) >= filter.min_star_rating!);
    }
    if (filter.kind?.length) {
      const kinds = filter.kind.map((k) => k.toLowerCase());
      arr = arr.filter((r) => kinds.includes((r.kind ?? "").toLowerCase()));
    }
    if (filter.meal_type?.length) {
      arr = arr.filter((r) => (r.rates ?? []).some((rate) => filter.meal_type!.includes(rate.meal_data?.value ?? rate.meal)));
    }
    if (filter.price_from !== undefined) {
      arr = arr.filter((r) => {
        const p = parseFloat(r.cheapestShowAmount ?? "");
        return !isNaN(p) && p >= filter.price_from!;
      });
    }
    if (filter.price_to !== undefined) {
      arr = arr.filter((r) => {
        const p = parseFloat(r.cheapestShowAmount ?? "");
        return !isNaN(p) && p <= filter.price_to!;
      });
    }
    return arr;
  }, [results, selectedAmenities, nameFilter, filter]);

  const sortedResults = useMemo(() => {
    const arr = [...filteredResults];
    const dir = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      if (sortField === "price") {
        const pa = parseFloat(a.cheapestShowAmount || a.cheapestRate?.show_amount || (a.cheapestRate?.payment_options?.payment_types?.[0]?.show_amount ?? ""));
        const pb = parseFloat(b.cheapestShowAmount || b.cheapestRate?.show_amount || (b.cheapestRate?.payment_options?.payment_types?.[0]?.show_amount ?? ""));
        if (isNaN(pa) && isNaN(pb)) return 0;
        if (isNaN(pa)) return 1;
        if (isNaN(pb)) return -1;
        return (pa - pb) * dir;
      }
      if (sortField === "rating") {
        return ((a.starRating ?? 0) - (b.starRating ?? 0)) * dir;
      }
      if (sortField === "name") {
        return a.name.localeCompare(b.name) * dir;
      }
      if (sortField === "distance") {
        if (!coordinates) return 0;
        const da = haversineKm(coordinates.lat, coordinates.lng, a.latitude, a.longitude);
        const db = haversineKm(coordinates.lat, coordinates.lng, b.latitude, b.longitude);
        return (da - db) * dir;
      }
      return 0;
    });
    return arr;
  }, [filteredResults, sortField, sortDir, coordinates]);

  const PAGE_SIZE = 12;
  const totalPages = Math.max(1, Math.ceil(sortedResults.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  if (safePage !== page) setPage(safePage);
  const paginatedResults = useMemo(
    () => sortedResults.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE),
    [sortedResults, safePage],
  );

  const hasSearched = results.length > 0 || searchError !== null;

  const hasServerFilters =
    !!filter.min_star_rating ||
    (filter.kind?.length ?? 0) > 0 ||
    (filter.meal_type?.length ?? 0) > 0 ||
    filter.price_from !== undefined ||
    filter.price_to !== undefined;

  const hasActiveFilters = hasServerFilters || selectedAmenities.length > 0 || !!nameFilter.trim();

  const resetFilters = () => {
    onFilterChange({});
    setSelectedAmenities([]);
    setNameFilter("");
  };

  const toggleAmenity = (a: string) =>
    setSelectedAmenities((prev) =>
      prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a],
    );

  return (
    <div className="flex flex-col h-full">
      {/* ── Search bar ── */}
      <div className="border-b bg-gray-50 px-4 py-3 flex-shrink-0">
        <div className="flex flex-wrap items-end gap-3">
          {/* Destination */}
          <div className="flex-1 min-w-[150px]">
            <Label className="text-sm font-medium mb-1 block">Destination</Label>
            <CityAutocomplete
              value={cityName}
              onChange={(v) => {
                setCityName(v);
                if (!v) setCoordinates(null);
              }}
              onPlaceSelect={(place) => {
                setCityName(place.address);
                setCoordinates({ lat: place.lat, lng: place.lng });
              }}
              placeholder="Search a city..."
            />
          </div>

          {/* Check-in */}
          <div className="w-[150px]">
            <Label className="text-sm font-medium mb-1 block">Check-in</Label>
            <DatePicker
              value={checkIn}
              onSelect={setCheckIn}
              placeholder="Check-in"
              minDate={startDate ? new Date(startDate) : undefined}
              maxDate={endDate ? new Date(endDate) : undefined}
            />
          </div>

          {/* Check-out */}
          <div className="w-[150px]">
            <Label className="text-sm font-medium mb-1 block">Check-out</Label>
            <DatePicker
              value={checkOut}
              onSelect={setCheckOut}
              placeholder="Check-out"
              minDate={checkIn ? new Date(checkIn) : startDate ? new Date(startDate) : undefined}
              maxDate={endDate ? new Date(endDate) : undefined}
            />
          </div>

          {/* Guests & rooms dropdown */}
          <div className="flex-1 min-w-[200px] max-w-[350px]" ref={guestsRef}>
            <Label className="text-sm font-medium mb-1 block">{t('ratehawk.guests')}</Label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setGuestsOpen((o) => !o)}
                className="flex items-center gap-1.5 w-full h-9 px-2.5 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
              >
                <Users className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                <span className="flex-1 text-left truncate">
                  {totalChildren > 0
                    ? `${totalAdults + totalChildren} (${totalAdults}+${totalChildren})`
                    : `${totalAdults} adult${totalAdults !== 1 ? "s" : ""}`}
                  {" · "}
                  {rooms.length} {rooms.length === 1 ? t('ratehawk.room') : t('ratehawk.rooms')}
                  {selectedCountry && <> · {selectedCountry.flag} {selectedCountry.name}</>}
                </span>
                <ChevronDown className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
              </button>
              {guestsOpen && (
                <div className="absolute z-50 top-full mt-1 left-0 bg-white border rounded-md shadow-lg p-3 w-[300px] flex flex-col gap-4 max-h-[420px] overflow-y-auto">
                  {rooms.map((room, roomIndex) => (
                    <div key={roomIndex} className="flex flex-col gap-2 pb-3 border-b last:border-b-0 last:pb-0">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-sm">{t('ratehawk.room')} {roomIndex + 1}</span>
                        {rooms.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeRoom(roomIndex)}
                            className="text-gray-400 hover:text-red-600"
                            title={t('ratehawk.removeRoom')}
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-sm">{t('ratehawk.adults')}</span>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => updateRoom(roomIndex, { adults: Math.max(1, room.adults - 1) })}
                            className="h-6 w-6 rounded border flex items-center justify-center hover:bg-gray-100"
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="w-5 text-center">{room.adults}</span>
                          <button
                            type="button"
                            onClick={() => updateRoom(roomIndex, { adults: Math.min(MAX_ADULTS_PER_ROOM, room.adults + 1) })}
                            className="h-6 w-6 rounded border flex items-center justify-center hover:bg-gray-100"
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-sm">
                          {t('ratehawk.children')}{" "}
                          <span className="text-xs text-gray-400">({t('ratehawk.childrenAgeRange')})</span>
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => removeChild(roomIndex, room.childrenAges.length - 1)}
                            disabled={room.childrenAges.length === 0}
                            className="h-6 w-6 rounded border flex items-center justify-center hover:bg-gray-100 disabled:opacity-40"
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="w-5 text-center">{room.childrenAges.length}</span>
                          <button
                            type="button"
                            onClick={() => addChild(roomIndex)}
                            disabled={room.childrenAges.length >= MAX_CHILDREN_PER_ROOM}
                            className="h-6 w-6 rounded border flex items-center justify-center hover:bg-gray-100 disabled:opacity-40"
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                      </div>

                      {room.childrenAges.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {room.childrenAges.map((age, childIndex) => (
                            <label key={childIndex} className="flex items-center gap-1 text-xs text-gray-500">
                              {t('ratehawk.childAge')} {childIndex + 1}
                              <Input
                                type="number"
                                min={0}
                                max={17}
                                value={age}
                                onChange={(e) => updateChildAge(roomIndex, childIndex, parseInt(e.target.value) || 0)}
                                className="w-14 h-7 text-center px-1"
                              />
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addRoom}
                    disabled={rooms.length >= MAX_ROOMS}
                    className="flex items-center justify-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 disabled:opacity-40 disabled:hover:text-blue-600"
                  >
                    <Plus className="h-3.5 w-3.5" /> {t('ratehawk.addRoom')}
                  </button>

                  <div className="border-t pt-3 flex flex-col gap-1">
                    <span className="text-xs font-medium text-gray-600">{t('ratehawk.nationality')}</span>
                    <span className="text-[10px] text-gray-400">{t('ratehawk.nationalityHint')}</span>
                    <AutocompleteSelect
                      options={COUNTRIES.map((c) => ({ value: c.code.toLowerCase(), label: c.name, icon: c.flag }))}
                      value={residency}
                      onValueChange={onResidencyChange}
                      placeholder={t('ratehawk.selectNationality')}
                      searchPlaceholder={t('ratehawk.searchNationality')}
                      filterMode="includes"
                      maxHeight="200px"
                      side="top"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Search button */}
          <Button
            onClick={onSearch}
            disabled={!canSearch || isSearching}
            className="h-9"
            size="sm"
          >
            {isSearching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            <span className="ml-1.5">{t('ratehawk.search')}</span>
          </Button>
        </div>

        {cityName && !coordinates && (
          <p className="text-sm text-amber-600 mt-1.5">
            Select a city from the suggestions to get coordinates
          </p>
        )}
      </div>

      {/* ── Body: sidebar + results ── */}
      <div className="relative flex flex-1 overflow-hidden min-h-0">
        {/* Left sidebar skeleton — first search only */}
        {isSearching && !hasSearched && (
          <aside className="hidden sm:flex w-56 flex-shrink-0 border-r bg-gray-50 overflow-y-auto flex-col">
            <div className="px-4 py-3 border-b">
              <Skeleton className="h-4 w-12" />
            </div>
            <div className="px-4 py-4 flex flex-col gap-6">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex flex-col gap-2">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-7 w-full" />
                  {i > 0 && <Skeleton className="h-4 w-full" />}
                  {i > 1 && <Skeleton className="h-4 w-4/5" />}
                </div>
              ))}
            </div>
          </aside>
        )}

        {/* Left sidebar — visible only after first search */}
        {hasSearched && (
          <>
            {isFiltersOpen && (
              <div
                className="sm:hidden absolute inset-0 z-30 bg-black/40"
                onMouseDown={() => setIsFiltersOpen(false)}
              />
            )}
          <aside
            className={`flex-shrink-0 border-r bg-gray-50 overflow-y-auto flex flex-col ${
              isFiltersOpen
                ? "absolute left-0 top-0 bottom-0 z-40 w-64 shadow-xl sm:relative sm:w-56 sm:shadow-none"
                : "hidden sm:flex sm:w-56"
            }`}
          >
            <div className="px-4 py-3 flex items-center justify-between border-b">
              <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{t('ratehawk.filters')}</span>
              <div className="flex items-center gap-2">
                {hasActiveFilters && (
                  <button
                    onClick={resetFilters}
                    className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                  >
                    <RotateCcw className="h-3 w-3" />
                    {t('ratehawk.resetFilters')}
                  </button>
                )}
                <button
                  onClick={() => setIsFiltersOpen(false)}
                  className="sm:hidden text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="px-4 py-4 flex flex-col gap-6">
              {/* Hotel name filter */}
              <div>
                <Label className="text-sm font-medium text-gray-700 block mb-2">{t('ratehawk.hotelName')}</Label>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                  <Input
                    value={nameFilter}
                    onChange={(e) => setNameFilter(e.target.value)}
                    placeholder={t('ratehawk.filterByName')}
                    className="pl-7 h-7 text-sm"
                  />
                  {nameFilter && (
                    <button
                      onClick={() => setNameFilter("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>

              {/* Min star rating (server-side) */}
              <div>
                <Label className="text-sm font-medium text-gray-700 block mb-2">{t('ratehawk.minRating')}</Label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setMinStarRating(star)}
                      className="focus:outline-none"
                      title={`${star}+ stars`}
                    >
                      <Star
                        className={`h-5 w-5 transition-colors ${
                          filter.min_star_rating && star <= filter.min_star_rating
                            ? "text-amber-400 fill-amber-400"
                            : "text-gray-300 fill-gray-100"
                        }`}
                      />
                    </button>
                  ))}
                </div>
                {filter.min_star_rating && (
                  <p className="text-xs text-gray-400 mt-1">{filter.min_star_rating}+ stars</p>
                )}
              </div>

              {/* Price range (server-side) */}
              <div>
                <Label className="text-sm font-medium text-gray-700 block mb-2">{t('ratehawk.priceRange')}</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={0}
                    placeholder="Min €"
                    value={filter.price_from ?? ""}
                    onChange={(e) => onFilterChange({ ...filter, price_from: e.target.value ? Number(e.target.value) : undefined })}
                    className="h-7 text-sm"
                  />
                  <span className="text-gray-400 text-sm">–</span>
                  <Input
                    type="number"
                    min={0}
                    placeholder="Max €"
                    value={filter.price_to ?? ""}
                    onChange={(e) => onFilterChange({ ...filter, price_to: e.target.value ? Number(e.target.value) : undefined })}
                    className="h-7 text-sm"
                  />
                </div>
              </div>

              {/* Hotel type / kind (server-side) */}
              <div>
                <Label className="text-sm font-medium text-gray-700 block mb-2">{t('ratehawk.propertyType')}</Label>
                <div className="flex flex-col gap-1.5">
                  {KIND_OPTIONS.map((k) => (
                    <label key={k} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(filter.kind ?? []).includes(k)}
                        onChange={() => toggleKind(k)}
                        className="rounded"
                      />
                      <span className="text-sm text-gray-700">{k}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Board type / meal (server-side) */}
              <div>
                <Label className="text-sm font-medium text-gray-700 block mb-2">{t('ratehawk.boardType')}</Label>
                <div className="flex flex-col gap-1.5">
                  {MEAL_OPTIONS.map((m) => (
                    <label key={m.value} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(filter.meal_type ?? []).includes(m.value)}
                        onChange={() => toggleMeal(m.value)}
                        className="rounded"
                      />
                      <span className="text-sm text-gray-700">{m.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Amenities */}
              {availableAmenities.length > 0 && (
                <div>
                  <Label className="text-sm font-medium text-gray-700 block mb-2">{t('ratehawk.amenities')}</Label>
                  <div className="flex flex-col gap-1.5">
                    {availableAmenities.map((a) => (
                      <label key={a} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedAmenities.includes(a)}
                          onChange={() => toggleAmenity(a)}
                          className="rounded"
                        />
                        <span className="text-sm text-gray-700">
                          {AMENITY_LABELS[a] ?? a.replace(/_/g, " ")}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Radius slider */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium text-gray-700">{t('ratehawk.radius')}</Label>
                  <span className="text-sm text-gray-500">{radius} km</span>
                </div>
                <Slider
                  min={1}
                  max={70}
                  step={1}
                  value={[radius]}
                  onValueChange={([v]) => setRadius(v)}
                  className="w-full"
                />
              </div>
            </div>
          </aside>
          </>
        )}

        {/* Results area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4">
            {!hasSearched && !isSearching && (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-3">
                <MapPin className="h-12 w-12 text-gray-200" />
                <p className="">{t('ratehawk.enterDestination')}</p>
              </div>
            )}

            {/* Results header — sempre visibile durante e dopo la ricerca */}
            {(isSearching || (hasSearched && !searchError)) && (
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                {isSearching ? (
                  <Skeleton className="h-4 w-48" />
                ) : (
                  <p className="text-gray-600">
                    {filteredResults.length}{" "}
                    {filteredResults.length === 1 ? "property" : "properties"}
                    {filteredResults.length < results.length && (
                      <span className="text-gray-400"> (filtered from {results.length})</span>
                    )}
                    {totalHotels != null && totalHotels > results.length && (
                      <span className="text-gray-400"> · {results.length} of {totalHotels} loaded</span>
                    )}
                  </p>
                )}
                <div className="flex items-center gap-2">
                  {hasSearched && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="sm:hidden h-8 gap-1.5"
                      onClick={() => setIsFiltersOpen(true)}
                    >
                      <SlidersHorizontal className="h-3.5 w-3.5" />
                      {t('ratehawk.filters')}
                      {hasActiveFilters && (
                        <span className="h-1.5 w-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                      )}
                    </Button>
                  )}
                  <SortResultsButton
                    sortField={sortField}
                    sortDir={sortDir}
                    onSortChange={(f, d) => { setSortField(f); setSortDir(d); setPage(0); }}
                    disabled={isSearching || filteredResults.length === 0}
                  />
                </div>
              </div>
            )}

            {isSearching && (
              <div className="flex flex-col gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <SearchResultCardSkeleton key={i} />
                ))}
              </div>
            )}

            {searchError && !isSearching && (
              <div className="flex flex-col items-center justify-center py-20">
                <X className="h-10 w-10 text-red-400 mb-4" />
                <p className="text-red-600 font-medium">Search failed</p>
                <p className="text-gray-500 mt-1">{searchError}</p>
              </div>
            )}

            {hasSearched && !isSearching && !searchError && (
              <>
                {sortedResults.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    {t('ratehawk.noHotelsMatch')}
                  </div>
                ) : (
                  <div className="flex flex-col gap-4">
                    {paginatedResults.map((result) => (
                      <SearchResultCard
                        key={result.hid}
                        result={result}
                        onSelect={onSelectResult}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Pagination footer - fixed */}
          {hasSearched && !isSearching && !searchError && totalPages > 1 && (
            <div className="border-t bg-white px-4 py-3 flex items-center justify-center gap-2 flex-shrink-0">
              <Button
                variant="outline"
                size="sm"
                disabled={safePage === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-gray-600">
                {safePage + 1} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={safePage >= totalPages - 1}
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
